import { Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Button } from '@vivaahealth/design-system';

import styles from './edit-insurance-page.module.css';
import GeneralDetailsForm from './GeneralDetailsForm';
import { GENERAL_DETAILS_SCHEMA, unableToReadMsgFunc } from './schema';
import { getCoverageStartDate } from './utils';

import { parseFieldValue } from '../../MultiInsurance';
import { maybeUpdateInsuranceDetails } from '../../MultiInsurance/utils';

import * as api from '../../../../../api';
import {
  getSubscribersFromGuarantorsPrimitive,
  mergeValues,
} from '../../../../../components/Guarantor/utils';
import * as store from '../../../../../reducers/store';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../../../utils/formSet';

interface StateProps {
  forms: store.Forms;
  patientInsuranceInformationPayors: store.Loaded['masterPracticePreference']['patientInsuranceInformationPayors'];
  exportInsuranceWithNullCoverageStartDateEnabled: store.Loaded['masterPracticePreference']['exportInsuranceWithNullCoverageStartDateEnabled'];
}

interface EditInsurancePageProps extends WithTranslation, StateProps {
  payorsDisplayNameMapping: Record<string, string>;
  cards: api.PatientInsuranceInformation.InsuranceInformationWithDetails[];
  encounterId: string;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  onSkipInsurance: () => Promise<void>;
  ocrResults: api.InsuranceCardOcrResult.InsuranceCardOcrResult | null;
  onSubmit: (
    values: api.PrevisitPatientInsurance.InsuranceCoverage,
  ) => Promise<void>;
  onCancel: () => Promise<void>;
}

const EditInsurancePage = ({
  cards,
  field,
  encounterId,
  forms,
  ocrResults,
  onSubmit,
  onCancel,
  patientInsuranceInformationPayors,
  payorsDisplayNameMapping,
  exportInsuranceWithNullCoverageStartDateEnabled,
  t,
}: EditInsurancePageProps) => {
  const parsedValue = parseFieldValue(field.value);
  const guarantorSubscribers = getSubscribersFromGuarantorsPrimitive(forms);
  const mergedSubscribers = mergeValues(
    parsedValue.subscribers,
    guarantorSubscribers,
  );
  const subscribersById = _.mapKeys(mergedSubscribers, 'id');
  const subscriberOrdering = mergedSubscribers.map((g) => g.id);

  const location = useLocation();
  const params = useParams<{ insuranceInformationId: string }>();

  const card = cards.find((card) => card.id === params.insuranceInformationId);

  if (!card) {
    return <Redirect to={'../' + location.search} />;
  }

  const getInitialPayor = () => {
    const initialPayor =
      card.payor ?? ocrResults?.insuranceProviderName ?? null;

    return patientInsuranceInformationPayors.includes(initialPayor as any)
      ? initialPayor
      : '';
  };
  const initialPayor = getInitialPayor();

  const initialValues: api.PrevisitPatientInsurance.InsuranceCoverage = {
    coverageStartDate:
      card.coverageStartDate ??
      getCoverageStartDate(
        exportInsuranceWithNullCoverageStartDateEnabled,
        ocrResults?.coverageStartDate,
      ),
    groupNumber: card.groupNumber ?? ocrResults?.groupNumber ?? null,
    id: card?.id ?? '',
    insuranceChanges: card.insuranceChanges,
    primaryPolicyHolderMemberId:
      card.primaryPolicyHolderMemberId ?? ocrResults?.memberId ?? '',
    payor: initialPayor,
    primary: card.primary ?? false,
    subscriberId: null,
  };

  const initialErrors = {
    ...(initialPayor !== null && {
      primaryPolicyHolderMemberId:
        initialValues.primaryPolicyHolderMemberId === ''
          ? unableToReadMsgFunc(t)
          : undefined,
    }),
  };

  return (
    <Formik
      initialErrors={initialErrors}
      initialValues={initialValues}
      validationSchema={GENERAL_DETAILS_SCHEMA(t)}
      onSubmit={async (values) => {
        const valueToSubmit = {
          ...parsedValue,
          subscribers: subscriberOrdering
            .map((subId) => {
              const subscriber = subscribersById[subId];
              return {
                ..._.omit(subscriber, 'newlyAdded'),
                // trim string values
                primaryPolicyHolderFirstName:
                  subscriber.primaryPolicyHolderFirstName?.trim() ?? null,
                primaryPolicyHolderLastName:
                  subscriber.primaryPolicyHolderLastName?.trim() ?? null,
                primaryPolicyHolderEmployer:
                  subscriber.primaryPolicyHolderEmployer?.trim() ?? null,
              };
            })
            .filter((subscriber) =>
              // currently selected subscriber should be valid, so clean up invalid entries
              _.every([
                subscriber.primaryPolicyHolderFirstName,
                subscriber.primaryPolicyHolderLastName,
                subscriber.id,
              ]),
            ),
          insuranceCoverages: [
            ...(parsedValue.insuranceCoverages || []),
            values,
          ],
        };

        await maybeUpdateInsuranceDetails(
          valueToSubmit,
          ocrResults!,
          encounterId,
        );

        await onSubmit(values);
      }}
    >
      {(
        formikProps: FormikProps<api.PrevisitPatientInsurance.InsuranceCoverage>,
      ) => {
        const isSubmitDisabled = !formikProps.isValid;
        return (
          <div className={styles['form-container']}>
            <GeneralDetailsForm
              formikProps={formikProps}
              patientInsuranceInformationPayors={
                patientInsuranceInformationPayors
              }
              payorsDisplayNameMapping={payorsDisplayNameMapping}
              subscribersById={subscribersById}
            />

            <Button
              fullWidth
              disabled={isSubmitDisabled}
              onClick={formikProps.submitForm}
            >
              {t('continue')}
            </Button>
            <Button fullWidth variant={'outlined'} onClick={onCancel}>
              {t('doNotAddThisCard', { ns: 'insurance' })}
            </Button>
          </div>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: store.FormSetSelected): StateProps => {
  const selectedPractice = getSelectedPracticePreference(state);
  return {
    forms: getSelectedForms(state.formSets, state.selectedFormSetId),
    patientInsuranceInformationPayors:
      selectedPractice.patientInsuranceInformationPayors,
    exportInsuranceWithNullCoverageStartDateEnabled:
      state.masterPracticePreference
        .exportInsuranceWithNullCoverageStartDateEnabled,
  };
};

export default connect(mapStateToProps)(
  withTranslation(['editInsurancePage', 'insurance'])(EditInsurancePage),
);
